import {
  INVITE_UNACCEPTED_DAYS,
  SWITCHABLE_MODULES,
  moduleDefinition,
  type AdminAttentionItem,
  type AdminRecentChange,
  type AdminSectionKey,
  type AdminSectionStatus,
  type ModuleKey,
} from '@nest-recruit/shared';
import { formatDate, formatNumber } from '../../../../lib/format';
import type { AdminSectionId } from '../../admin-sections';

// ADMIN-21's words, kept out of the components so each rule reads in one place and is tested on its own.

export function plural(count: number, one: string, many: string): string {
  return `${formatNumber(count)} ${count === 1 ? one : many}`;
}

/** "Good morning" by the clock where the Admin is, not where the server is. */
export function greeting(now: Date, timeZone: string): string {
  let hour = now.getHours();
  try {
    hour = Number(new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hourCycle: 'h23', timeZone }).format(now));
  } catch {
    // An unknown zone name: the browser's own clock is close enough for a greeting.
  }
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export function lede(needsYou: number, firstName: string, hello: string): string {
  const who = firstName === '' ? hello : `${hello}, ${firstName}`;
  if (needsYou === 0) return `${who}. Nothing needs you; everything is running normally.`;
  const things = needsYou === 1 ? '1 thing needs you' : `${formatNumber(needsYou)} things need you`;
  return `${who}. ${things}; everything else is running normally.`;
}

// --- "Needs your attention" ---------------------------------------------------------------------

export function attentionTitle(item: AdminAttentionItem): string {
  switch (item.kind) {
    case 'failed_emails_today':
      return `${plural(item.count, 'email', 'emails')} failed to send today`;
    case 'invites_unaccepted':
      return `${plural(item.count, 'invitation', 'invitations')} not accepted after ${INVITE_UNACCEPTED_DAYS} days`;
    case 'holidays_missing':
      return `Public holidays for ${item.year} not added`;
    case 'anonymising_soon':
      // A date already passed reads "are due", never "will be … from" a day gone by.
      return new Date(item.firstDueAt).getTime() <= Date.now()
        ? `${plural(item.count, 'candidate record is', 'candidate records are')} due to be anonymised`
        : `${plural(item.count, 'candidate record', 'candidate records')} will be anonymised from ${formatDate(item.firstDueAt)}`;
    case 'starter_templates':
      return `${plural(item.count, 'message template still has', 'message templates still have')} the starter wording`;
  }
}

function andMore(listed: number, count: number): string {
  return count > listed ? ` and ${formatNumber(count - listed)} more` : '';
}

/** The plain line under the title: what it is and why it matters. Null for the invites, which list each person. */
export function attentionDetail(item: AdminAttentionItem): string | null {
  switch (item.kind) {
    case 'failed_emails_today': {
      const first = item.examples[0];
      if (!first) return 'The people they were for have not been told.';
      const reason = first.failureReason ? ` (${first.failureReason})` : '';
      return `"${first.subject}" to ${first.toAddress}${reason}${andMore(1, item.count)}. The people they were for have not been told.`;
    }
    case 'invites_unaccepted':
      return null;
    case 'holidays_missing': {
      const names = item.calendars.map((calendar) => calendar.name).join(', ');
      return `${names}${andMore(item.calendars.length, item.count)}. Interview booking will offer those days as normal working days.`;
    }
    case 'anonymising_soon':
      return item.isDryRun
        ? 'Retention is on a dry run, so nothing is removed yet: the run only reports who would be.'
        : 'They reach the end of the keep period. Names and contact details are removed; hiring numbers stay.';
    case 'starter_templates': {
      const names = item.templates.map((template) => `"${template.name}"`).join(', ');
      return `${names}${andMore(item.templates.length, item.count)}. Candidates see these words.`;
    }
  }
}

// --- "Where things stand" -----------------------------------------------------------------------

export const SECTION_OF_STATUS: Readonly<Record<AdminSectionKey, AdminSectionId>> = {
  people: 'people',
  roles: 'roles',
  delegation: 'delegation',
  access_log: 'access-log',
  pipelines: 'pipelines',
  reasons: 'reasons',
  sources: 'sources',
  calendars: 'calendars',
  rooms: 'rooms',
  message_templates: 'message-templates',
  email_delivery: 'email',
  interview_plans: 'interview-plans',
  role_templates: 'role-templates',
  retention: 'retention',
};

export interface StatusWords {
  readonly text: string;
  /** Something there needs a look: drawn with a warning mark, the words staying ink. */
  readonly needsLook: boolean;
}

export function sectionStatus(status: AdminSectionStatus): StatusWords {
  const calm = (text: string): StatusWords => ({ text, needsLook: false });
  switch (status.key) {
    case 'people':
      return calm(`${plural(status.active, 'person', 'people')} · ${formatNumber(status.invited)} invited`);
    case 'roles':
      return calm(plural(status.roles, 'role', 'roles'));
    case 'delegation':
      return calm(status.activeNow === 0 ? 'Nobody away now' : `${plural(status.activeNow, 'cover', 'covers')} running now`);
    case 'access_log':
      return calm(`${plural(status.entriesLast30Days, 'entry', 'entries')} in 30 days`);
    case 'pipelines':
      return calm(plural(status.pipelines, 'pipeline', 'pipelines'));
    case 'reasons':
      return calm(plural(status.reasons, 'reason', 'reasons'));
    case 'sources':
      return calm(plural(status.sources, 'source', 'sources'));
    case 'calendars':
      return status.missingHolidayYear !== null
        ? { text: `${status.missingHolidayYear} holidays missing`, needsLook: true }
        : calm(plural(status.calendars, 'calendar', 'calendars'));
    case 'rooms':
      return calm(`${plural(status.rooms, 'room', 'rooms')} in ${plural(status.ventures, 'venture', 'ventures')}`);
    case 'message_templates':
      return status.starterWording > 0
        ? { text: `${formatNumber(status.starterWording)} on the starter wording`, needsLook: true }
        : calm(plural(status.templates, 'template', 'templates'));
    case 'email_delivery':
      return status.failedToday > 0
        ? { text: `${formatNumber(status.failedToday)} failed today · ${formatNumber(status.deliveredToday)} delivered`, needsLook: true }
        : calm(`${formatNumber(status.deliveredToday)} delivered today`);
    case 'interview_plans':
      return calm(plural(status.plans, 'plan', 'plans'));
    case 'role_templates':
      return calm(plural(status.templates, 'template', 'templates'));
    case 'retention': {
      const kept = `Kept ${plural(status.periodDays, 'day', 'days')}`;
      const mode = status.mode === 'dry_run' ? ' · dry run' : '';
      const due = status.dueThisMonth > 0 ? ` · ${formatNumber(status.dueThisMonth)} due this month` : '';
      return calm(`${kept}${due}${mode}`);
    }
  }
}

// --- "Recent changes" ---------------------------------------------------------------------------

export interface ChangeWords {
  readonly actor: string;
  /** "added the room", "switched on". */
  readonly action: string;
  /** The thing changed, drawn bold; null when the action already says it all. */
  readonly subject: string | null;
}

/** The thing a `config.*` or other admin event is about, for an event that names no record. */
const NOUNS: Readonly<Record<string, string>> = {
  approval_chain: 'approval chain',
  career_site: 'career site',
  custom_field: 'custom field',
  fx_rates: 'exchange rates',
  kit: 'interview kit',
  name_equivalences: 'name matching',
  pipeline: 'pipeline',
  plan: 'interview plan',
  reasons: 'reasons',
  retention: 'retention',
  rubric: 'rubric',
  sources: 'sources',
  system: 'system settings',
  tags: 'tags',
  calendar: 'calendar',
  resource: 'room',
  offer_letter: 'offer letter',
  template: 'message template',
  tool_name: 'tool name',
  staff_list: 'staff list',
  metric_dictionary: 'metric dictionary',
  role: 'role',
};

const VERBS: Readonly<Record<string, string>> = {
  created: 'added',
  updated: 'changed',
  archived: 'archived',
  deleted: 'deleted',
  added: 'added',
  hidden: 'hid',
  merged: 'merged',
  restored: 'restored',
  replaced: 'replaced',
  holiday_added: 'added a holiday to',
  holiday_removed: 'removed a holiday from',
  field_rule_changed: 'changed the hidden fields of',
  transition_rule_changed: 'changed the stage rules of',
};

function textOf(value: unknown, keys: readonly string[]): string | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  for (const key of keys) {
    const found = record[key];
    if (typeof found === 'string' && found.trim() !== '') return found;
  }
  return null;
}

function isModuleKey(value: unknown): value is ModuleKey {
  return typeof value === 'string' && (SWITCHABLE_MODULES as readonly string[]).includes(value);
}

/**
 * One event-log row in plain words: "You switched on Compare", "Tahmina Rahman added the room Board room". The
 * names come from the event's own before and after; an event that names nothing says what kind of thing it was.
 */
export function describeChange(change: AdminRecentChange, viewerId: string | null): ChangeWords {
  const actor = change.actorId !== null && change.actorId === viewerId ? 'You' : (change.actorName ?? 'The system');
  const after = change.payloadAfter ?? change.payloadBefore;

  if (change.eventType === 'settings.module_toggled') {
    const module = textOf(after, ['module']);
    const enabled = typeof after === 'object' && after !== null && (after as Record<string, unknown>).enabled === true;
    return { actor, action: enabled ? 'switched on' : 'switched off', subject: isModuleKey(module) ? moduleDefinition(module).label : 'a part of the app' };
  }
  if (change.eventType === 'user.invited') {
    return { actor, action: 'invited', subject: textOf(after, ['fullName', 'email']) ?? 'somebody' };
  }
  if (change.eventType === 'resource.restored') {
    // A room taken off and put back with Undo (Rooms, 2026-11-02).
    return { actor, action: 'put back the room', subject: textOf(after, ['name']) ?? 'a room' };
  }
  if (change.eventType === 'config.fx_rates_updated') return describeRateChange(actor, after);
  if (change.eventType === 'config.system_updated') {
    // The payload is the whole of the settings, so a `name` in it is not what changed.
    return { actor, action: 'changed', subject: 'the system settings' };
  }

  const [family = '', rest = ''] = change.eventType.split('.');
  // `config.pipeline_created` names its thing in the second half; `calendar.created` in the first.
  const configMatch = family === 'config' ? /^(.*)_(created|updated)$/.exec(rest) : null;
  const nounKey = configMatch ? (configMatch[1] ?? '') : family;
  const verbKey = configMatch ? (configMatch[2] ?? '') : rest;
  const noun = NOUNS[nounKey] ?? change.entityType.replaceAll('_', ' ');
  const verb = VERBS[verbKey] ?? 'changed';
  // Review R23: a role change names its role, whatever the payload carries (a renamed role's payload has its new name).
  const name = textOf(after, ['name', 'label', 'title', 'fullName']) ?? change.entityLabel ?? null;

  if (name !== null) return { actor, action: `${verb} the ${noun}`, subject: name };
  // "added a holiday to" already carries its object; a plain verb takes the kind of thing as its subject.
  const isPhrase = verb.includes(' ');
  const article = /^[aeiou]/.test(noun) ? 'an' : 'a';
  return { actor, action: isPhrase ? `${verb} ${article} ${noun}` : verb, subject: isPhrase ? null : `the ${noun}` };
}

const RATE_VERBS: Readonly<Record<string, string>> = { added: 'added', removed: 'removed', restored: 'put back' };

/** `config.fx_rates_updated` carries one rate: "You added the exchange rate USD→BDT", "removed", "put back". */
function describeRateChange(actor: string, after: unknown): ChangeWords {
  const action = textOf(after, ['action']);
  const from = textOf(after, ['fromCurrency']);
  const to = textOf(after, ['toCurrency']);
  const verb = (action ? RATE_VERBS[action] : undefined) ?? 'changed';
  if (from && to) return { actor, action: `${verb} the exchange rate`, subject: `${from}→${to}` };
  return { actor, action: verb, subject: 'the exchange rates' };
}
